/**
 * The Data guide: what the dashboard's data is, where it is kept and what
 * every field means.
 *
 * Two readers. A person opens it in the dashboard. A language model is handed
 * it as a file beside an exported JSON, so it reads the numbers correctly.
 * Both get the same words: `README_MARKDOWN` is the one source, and
 * `DataGuide` draws it.
 *
 * Keep it true. When a field, a rule or the storage changes, change the text
 * here in the same commit. The facts come from:
 *   - the gather: lambdas/python/numa-fleet-analytics-rollup (gather/, fleet_index.py)
 *   - the sums: ../shared.ts, ../inferredCosts.ts, ../selectors.ts
 *   - the exports: ../dashboardExport.ts and pages/NumaDashboard.tsx
 *
 * The text uses no Markdown tables: the portal's Markdown renderer does not
 * draw them. Lists and code blocks read the same on screen and in the file.
 */
import ReactMarkdown from 'react-markdown';
import './DataGuide.css';

export const README_MARKDOWN = `
# Numa Dashboard data guide

Read this before you work a number out from the dashboard's data or from one of its exports. It says where the data is kept, what every field means, and how the dashboard gets each figure. Several figures are not what their name suggests. This guide says which.

It has two readers: a person, and a language model that is handed an exported file with it.

## The data in one page

- The dashboard shows one **snapshot** for each client stack. A snapshot is one JSON object. The nightly rollup rebuilds it from the client's own AWS account.
- A snapshot holds up to 420 days of history. The AWS bill reaches back 14 calendar months at most: AWS gives no more.
- An Arcanum AWS account with no Numa stack in it also has a snapshot. Its name starts \`_infra:\`. It holds an AWS bill and nothing else.
- A fleet-wide view ("All customers", "Arcanum's bill") is stored nowhere. The dashboard adds the snapshots up in the browser.
- A client tagged Inactive is hidden from every list and from every use and credit figure, in every view. Its spend stays in every view's spend, because the money was spent. See "Inactive clients".
- Only a client Arcanum bills for credits can owe credits. See "Who owes credits".
- Money is in US dollars everywhere in the data. The dashboard converts it for display only.
- Every day key is a UTC day, written \`YYYY-MM-DD\`.
- A snapshot holds people's email addresses and the names people gave their agents, schedules and apps. Treat every export as customer data. See "What a snapshot holds about people and their content".

## Where the data is stored

Everything is in the S3 bucket \`numa-portal-fleet-analytics-snapshots\`, in the deployer AWS account.

- \`latest/<client>.json\`: the client's complete snapshot. This is the source of truth. The dashboard loads it when you select one stack.
- \`dated/<client>/<YYYY-MM-DD>.json\`: the same snapshot as it was each night. A copy is deleted 180 days after it is written.
- \`fleet/<tier>.json.gz\`: the **fleet index**. It is every current snapshot, split across seven files so the dashboard opens quickly. The fleet-wide views load these.

### The fleet index

The nightly run builds the index as its last step, from the snapshots in \`latest/\`. The seven files:

- \`core\`: everything not listed under another file. The daily totals, the splits by category, by model and by tool, credits, counts, and the client's settings. The dashboard loads it first.
- \`users\`: one row per person, every per-person daily map, and the map from a person's id to an email address.
- \`agents\`: one row per agent, every per-agent daily map, and the costliest scheduled runs.
- \`services\`: the AWS bill by service and day.
- \`inventory\`: the long lists. Agents, schedules, knowledge bases, and who has connected which integration.
- \`apps\`: one row per app, the per-app daily maps, and each person's use of apps.
- \`conversations\`: the costliest conversations.

The exact fields each file takes out of \`core\`:

\`\`\`
users          chat.by_user
               chat.daily_cost_by_user
               chat.daily_messages_by_user
               chat.daily_convs_started_by_user
               chat.daily_tool_calls_by_user
               chat.daily_turns_by_user
               chat.daily_span_seconds_sum_by_user
               chat.daily_span_count_by_user
               users.sub_to_email
agents         chat.by_agent
               chat.daily_cost_by_agent
               chat.daily_adhoc_cost_by_agent
               chat.daily_scheduled_cost_by_agent
               chat.daily_scheduled_count_by_agent
               chat.daily_adhoc_count_by_agent
               scheduled_runs.by_agent
               scheduled_runs.top_runs
               impact.per_agent_runs
               impact.daily_time_saved_minutes_by_agent
services       cost_explorer.by_service_daily
               cost_explorer.bedrock_usage_type_daily
inventory      agents.agents.rows
               agents.user_agents.rows
               schedules.rows
               schedules.schedule_to_agent
               integrations.knowledge_bases.rows
               pipedream.by_app_active
               pipedream.per_user_active
apps           user_apps.rows
               user_apps.daily_by_app
               user_apps.users
conversations  chat.top_conversations
               credits.top_conversations
\`\`\`

\`core\` carries this list too, as \`tier_paths\`, so a reader can learn it from the file.

- **Nothing is lost in the split.** A field moves whole to one file and keeps its path. To get a client's snapshot back, deep-merge its part from each of the seven files. Where a field is in two files, \`core\` wins. The build checks this for every client on every run, and uploads nothing when the check fails.
- **Standalone customers** keep \`cost_explorer.by_service_daily\` in \`core\` as well as in \`services\`. The dashboard needs it at once to take the customer's own systems out of the bill.
- **Size**, measured on 13 October 2026: seven files, 8.4 MB compressed in total, \`core\` 2.2 MB. The index held 322 members: 217 client stacks and 105 infrastructure accounts.

Each file is gzip. Its shape:

\`\`\`
{
  "schema": 1,
  "tier": "core",
  "generated_at": "2026-10-13T01:10:47Z",   // when the index was built
  "window_days": 420,
  "clients": { "<client name>": { ...this file's fields, at their paths in the snapshot } },

  // core only:
  "tier_paths": { "users": ["chat.by_user", ...], "agents": [...], ... },
  "tier_bytes": { "core": 2212401, "users": 728300, ... },   // compressed size of each file
  "status": {
    "<client name>": {
      "ok": true,
      "last_attempt_at": "2026-10-12T12:20:00Z",
      "last_ok_at": "2026-10-12T12:20:00Z",   // the time of the snapshot in the index
      "last_error": ""                         // first 500 characters, credentials removed
    }
  }
}
\`\`\`

### Who is in the index

- In: every client in the client list that has a stored snapshot, and every infrastructure account that no client stack uses.
- Left out: a stack that was removed from the client list, and an infrastructure account that a client stack now holds. Their files stay in S3. Nothing is deleted.
- A client whose gather failed tonight stays in, with its last good snapshot.

### The old DynamoDB table

Earlier versions of the rollup also wrote a cut copy of each snapshot to the DynamoDB table \`numa-portal-fleet-analytics\`. A large client's copy went over DynamoDB's 400 KB limit for one item and stopped updating, and fleet totals read low. The rollup no longer writes to the table. Its rows are left as they are.

The dashboard reads the table only when the bucket holds no index. A row there is not a complete snapshot: the per-person, per-agent and per-service daily maps hold 92 days, and it lists 15 conversations, not 50.

## How often the data updates

- Every night at 01:00 New Zealand time. That is after the nightly credit settlement at 00:30.
- The state machine \`numa-fleet-analytics-rollup-orchestrator\` runs it. The steps: list the clients, gather each client (five at a time), read the infrastructure accounts, build the index, copy the data into HQ's files.
- The gather for one client reads that client's own AWS account: the AWS bill, every chat trace, the chat history, schedules and their run logs, agents, knowledge bases, data connectors, the list of people, connected integrations, the credit ledger, and apps.
- \`generated_at\` on a snapshot is when its gather finished. \`generated_at\` on an index file is when the index was built.

## A failed stack and a stale stack

- **Failed**: \`status.<client>.ok\` is false. The last gather did not finish, and \`last_error\` says why. The snapshot in the index is the last good one. \`last_ok_at\` is its time.
- **Stale**: the snapshot's \`generated_at\` is more than 36 hours old.
- A client with a \`status\` entry and no snapshot has never had a good gather. It is in no view's numbers.

What an old snapshot does to the numbers:

- **Its history is still summed into every view it belongs to.** Its days after its last update hold nothing. So a view with a stale stack reads low for recent days. Example: a stack that last updated on 1 September adds nothing to a window of 1 to 30 October. The view is short by all of that stack's October use.
- **Its "now" figures are as they stood at its last update:** people, agents, schedules, credit balance.
- **"Credits used this month" leaves it out** when its \`credits.current_month\` is not the current billing month. Its August use is never read as October's.
- **The health map measures it over the 30 days up to its own snapshot day**, and draws it in grey. It is never drawn as a client that nobody uses.
- **A summed view's time is the time of its oldest snapshot.** A sum is no fresher than its oldest part.

## Rules for every number

1. **A daily map** looks like \`{"2026-10-01": 12, "2026-10-03": 4}\`. A day with nothing has no key. Read a missing day as 0.
2. **A window sum** is the sum of the days from the start day to the end day, both included.
3. **A missing field is not zero.** A field that was added later is missing on a snapshot written before it. When a daily map is missing, the dashboard shows the lifetime total with an "all time" tag. Never treat a lifetime total as the window's. When the map is present and the window holds no days, the answer is 0.
4. **A fleet-wide view is a sum you can repeat.** See "How a view is added up".
5. **The tags on screen.** No tag: the number follows the time window. "now": as it stood at the last nightly update. "this month": the current New Zealand billing month. "last 30 days": the 30 days up to the snapshot's own day. "all time": everything on record, because the number cannot follow the window.

## Spend

The dashboard has three spend numbers. They answer three different questions.

- **AWS bill**: what AWS billed the stack's account. \`cost_explorer\`.
- **Model spend from chat and agents**: what the stack's model use cost, priced by Numa from token counts. \`chat.daily_cost\`.
- **Total spend**: what the stack cost to run. The dashboard works it out from the other two.

### The AWS bill: \`cost_explorer\`

- Read from AWS Cost Explorer, as unblended cost, by day and by service.
- \`totals_by_day\`: \`{day: usd}\`. \`totals_by_service\`: \`{service: usd}\` over the whole snapshot. \`grand_total\`: their sum.
- \`by_service_daily\`: \`{service: {day: usd}}\`. In the \`services\` file.
- \`model_lines_daily\`: \`{day: usd}\`. The sum of the bill's model lines for each day. See "Total spend" for what a model line is.
- \`bedrock_usage_type_totals\` and \`bedrock_usage_type_daily\`: the "Amazon Bedrock" line by usage type. That line is embeddings and small models. It is not the model lines.
- \`window\`: \`{start, end, days}\`, the days the bill covers.
- The bill stops a day or two before today. The newest day can read low until AWS settles it.
- A snapshot written before the tiered index has no \`model_lines_daily\` and no \`bedrock_usage_type_daily\`. The dashboard then adds the model lines up from \`by_service_daily\`.

### Model spend priced from tokens: \`chat.daily_cost\`

Numa prices each request itself, from the token counts in the chat trace.

- **Why not the cost the trace reports?** The agent software prices every cache write at the 5-minute rate. Numa uses the 1-hour cache, which costs more. So the reported cost is too low: on a stack that mostly uses Sonnet, the real cost is about 1.2 times it. \`chat.totals.sdk_cost\` keeps the reported figure for comparison.
- **Sub-agents are included.** A newer trace lists tokens per model, sub-agents included, and the price is worked out per model.
- **Before 22 May 2026** requests went through a regional route that costs 10% more. Requests before that day are priced 10% higher.
- **Some models are not priced from tokens.** For the Standard and Lite tiers, the Nolia model tiers, and Haiku 5.5 as the main model, the cost in the trace is the real charge and is used as it is.
- **A model on neither list is priced at the Sonnet 4.6 rate.** That can be far from its real price. Before you trust a stack's model spend, check \`chat.model_totals\` for ids that are not Claude and not a Numa tier.
- **A conversation with no trace** keeps the cost its chat-history row reports, on the day of its last request.

### Total spend: what it adjusts and why

Clients share model capacity. One client's model use is often billed to another client's AWS account. So an AWS bill does not say what a stack's own model use cost:

- A stack that borrows shows almost no model spend on its bill, though it used plenty.
- A stack that lends shows its own model spend plus every borrower's.

So the dashboard takes the model lines out of the AWS bill and puts in the model spend Numa priced from that stack's own tokens. Every other line of the bill stays as billed.

\`\`\`
total spend[day] = AWS bill[day] - model lines[day] + model spend from tokens[day]
lent[day]        = max(0, model lines[day] - model spend from tokens[day])
borrowed[day]    = max(0, model spend from tokens[day] - model lines[day])
\`\`\`

A **model line** is a bill line whose service name matches \`Claude ... (Amazon Bedrock Edition)\` or \`OpenAI ... (Amazon Bedrock Edition)\`. "Amazon Bedrock" (embeddings) and "Amazon Bedrock AgentCore" (compute) are not model lines. They stay in the bill.

Example, one stack, one day:

\`\`\`
AWS bill                   100.00
  of which model lines      10.00    the stack borrows, so little is billed here
model spend from tokens     60.00
total spend                150.00    = 100 - 10 + 60
borrowed                    50.00    = 60 - 10
\`\`\`

- **Role.** Over a window a stack is a \`borrower\` when borrowed is 10% of its model spend or more, a \`lender\` when lent is, \`mixed\` when both are, and \`standalone\` when neither is. This \`standalone\` means "neither lends nor borrows". It has nothing to do with a standalone customer.
- **The sum over a view.** Lending and borrowing cancel out inside a view that holds both sides. Total spend for "Arcanum's bill" is close to the AWS bill for those accounts.
- **When the model lines are not known.** A snapshot with a bill and no breakdown by service has unknown model lines. Total spend would then count model use twice. The dashboard loads the \`services\` file before it shows the number.
- Use total spend to compare stacks. Use the AWS bill only to answer "what did AWS bill this account".

### Standalone customers: Numa's part of the bill

A standalone customer (\`client_config.account_org\` is \`standalone\`) runs Numa in an AWS account that also runs their own systems. Their AWS bill is not Numa's cost. The dashboard keeps the services Numa deploys, plus the model lines, and drops the rest.

- Kept: Amazon Bedrock, Amazon Bedrock AgentCore, Amazon Q, Amazon Q Business, AWS Lambda, AWS Step Functions, Amazon DynamoDB, Amazon Glacier, Amazon SimpleDB, Amazon OpenSearch Serverless, Amazon API Gateway, Amazon CloudFront, Amazon Route 53, Amazon Cognito, AmazonCloudWatch, CloudWatch Events, AWS Key Management Service, AWS Secrets Manager, AWS Identity and Access Management, Amazon EC2 Container Registry (ECR), Amazon Simple Notification Service, Amazon Simple Queue Service, Amazon Simple Workflow Service, AWS Cost Explorer, Amazon Transcribe, Amazon Comprehend, Amazon Textract, Amazon Simple Email Service, and every model line.
- Dropped: everything else. That includes Amazon S3, EC2, load balancers, RDS, the provisioned OpenSearch Service and AWS Glue. Numa's own S3 spend is a few dollars a month for each client, and a standalone S3 bill is mostly the customer's own storage.
- The filter is applied in the browser. **The snapshot in S3 holds the customer's whole AWS bill.** If you add up \`cost_explorer\` from a raw snapshot for a standalone customer, you get their whole bill, not Numa's cost.
- The dashboard records what it dropped: \`non_numa_filtered_total\` and \`non_numa_filtered_daily\`. The rollup never writes these two.
- NextGen and Arcanum accounts run nothing but Numa. Their whole bill is Numa's cost and nothing is dropped.

### Shared AWS accounts are counted once

AWS bills by account. Several stacks can sit in one account: the dev stacks share one, and a few NextGen accounts hold two stacks. Each of those stacks' snapshots carries the same bill.

- **In a summed view** the bill is taken once for each AWS account, from one snapshot in that account: the **cost owner**. The cost owner is an active stack when the account has one, and the freshest snapshot among those. So a hidden Inactive stack never takes an active client's bill with it. Chat, people, schedules, credits and apps are summed for each stack.
- **In the Clients table** the stacks of a shared account are one row, with the account's bill once and each stack's own figures added up. The row names its stacks.
- **If you add snapshots up yourself**, group by \`client_config.client_account_id\` and take \`cost_explorer\` once for each group. If you do not, you count a shared account's bill two or three times.

Example: two stacks in one account with a bill of 400. A view that holds both counts 400, not 800.

## Chat: conversations, messages and people

The \`chat\` block is built from every chat trace in the client's storage, joined with the chat-history table. It covers every conversation whose last activity is inside the 420 days.

### The three kinds of conversation

- \`agent_run\`: the conversation id starts \`schedule-\`. A schedule or an event started it.
- \`agent_chat\`: it has an agent, and a person started it.
- \`plain_chat\`: no agent. A person chatting with Numa.

\`chat.by_category\` holds the lifetime totals for each. The \`daily_*_by_category\` maps hold them by day.

### A conversation is counted on the day it started

- \`chat.daily_convs_started\`: each conversation once, on the day of its first request. **This is the count to use.** It sums to \`chat.totals.convs\`.
- \`chat.daily_convs\`: each conversation once for every day it was active. These are conversation-days. A sum of them over a window is too high. The field is kept only for older tools that read it.

Example: one conversation active on 1, 2 and 5 October.

\`\`\`
daily_convs_started   {"2026-10-01": 1}                                        sums to 1
daily_convs           {"2026-10-01": 1, "2026-10-02": 1, "2026-10-05": 1}      sums to 3
\`\`\`

On one stack the conversation-days came to 7,702 against 7,156 real conversations.

- \`daily_convs_started_by_category\` and \`daily_convs_started_by_user\` split the same count.
- \`daily_convs_started_by_model\` covers only conversations that have a model on record. It can sum to a little less than the total.
- \`daily_scheduled_count\` and \`daily_adhoc_count\` still count conversation-days, split by whether a schedule started the conversation.
- A snapshot written before \`daily_convs_started\` existed has only \`daily_convs\`. The dashboard then shows the count with "about" in front.

### Which day a value lands on

- **The event's own day:** cost, messages, turns, tool calls, requests, errors, the four token series, tool calls by tool, and the per-person turns and tool calls. A conversation that runs for a week puts each day's cost on that day.
- **The conversation's first request day:** conversations started and its three splits, the two conversation-length series, and the two per-agent count maps.
- **A value with no date.** A conversation with no dated request is placed on its first active day, then its last request day, then its last activity day. For requests, errors, tokens and tool calls by tool, the part of a conversation's total that no dated event accounts for goes on its last request day.

### Sums that match the totals, and sums that do not

Checked on one dev stack, on the 349 traces that changed in 14 days and all 837 of its conversations. It was not checked on a full 420 days.

- **Sum to the total exactly:** \`daily_requests\`, \`daily_errors\`, the four token series, \`daily_tool_calls_by_tool\` (to \`tool_totals\`, tool by tool), \`daily_convs_started\` (to \`totals.convs\`), and the two per-agent count maps (to \`by_agent[].scheduled_count\` and \`adhoc_count\`).
- **Do not, by design:**
  - \`daily_turns\` and \`daily_tool_calls\` hold only what has a dated event. They can be short of \`totals.turns\` and \`totals.tool_calls\`. On the sample: 15,784 against 16,012 turns, and 13,867 against 14,109 tool calls.
  - \`daily_turns_by_user\` and \`daily_tool_calls_by_user\` sum to \`daily_turns\` and \`daily_tool_calls\`, so they are short by the same amount.
  - \`daily_convs_started_by_model\` leaves out conversations with no model: 817 of 837 on the sample.
  - \`daily_convs\` sums to more than \`totals.convs\`, as above.

### Messages

- \`daily_messages\` counts messages a person typed. Text that the agent software adds when it loads a skill is not counted.
- **A schedule's prompt counts as a message from the schedule's owner.** So a scheduled run adds one message for its owner on the day it runs.
- Cost per message is model spend divided by messages, both summed over the same window.

### People

- **Provisioned**: \`users.provisioned_users\`. People with a login. "now".
- **Active in a window**: people with a message on a day inside the window, each counted once. It is the number of ids in \`chat.daily_messages_by_user\` with a day in the window. It needs the \`users\` file.
- Because a schedule's prompt counts as its owner's message, a person whose schedule ran in the window is active, whether or not they opened Numa.
- \`chat.daily_active_users\`: people active on each day. **Never add these days up.** A person active on ten days would count ten times.
- \`chat.totals.users\`: people with a conversation anywhere in the snapshot. All time.
- \`users.modified_in_last_30d\` is the number of logins whose record changed in 30 days. It is not active people. The dashboard does not show it.

Example: Ana sends messages on 1 and 2 October, and Ben on 2 October.

\`\`\`
daily_active_users   {"2026-10-01": 1, "2026-10-02": 2}     the days add up to 3
active in 1 to 2 October = 2                               Ana and Ben, once each
\`\`\`

### Conversation length

- A conversation's length is the seconds from its first request to its last. A conversation with one request has length 0. One with no dated request has no length.
- \`daily_span_seconds_sum\` and \`daily_span_count\`, both on the first request day. Average length over a window is the sum of the first divided by the sum of the second.
- Per person: \`daily_span_seconds_sum_by_user\` and \`daily_span_count_by_user\`. Divide by the span count, not by conversations started: a conversation with no length is in the second and not in the first.

### Requests, errors, turns, tools and tokens

- A **request** is one piece of work the agent finished: one \`result\` event in the trace. \`daily_requests\`.
- An **error** is a request that ended in an error. \`daily_errors\`. Error rate is errors divided by requests over the same window.
- A **turn** is one step the agent took inside a request. \`daily_turns\`.
- A **tool call** is one use of a tool. \`daily_tool_calls\` is the count by day. \`daily_tool_calls_by_tool\` is \`{tool: {day: n}}\`. \`tool_totals\` is the lifetime count for each tool.
- **Tokens:** \`daily_input_tokens\`, \`daily_output_tokens\`, \`daily_cache_read_tokens\`, \`daily_cache_creation_tokens\`.
- **Cache hit ratio** is cache read tokens divided by input, cache read and cache creation tokens together.

Example: input 200, cache read 700, cache creation 100. The ratio is 700 / 1,000 = 70%.

### Models

- A conversation is counted under one model: the model of its most recent session start. A conversation that changed model puts all its cost and its one count there. Sub-agent spend lands on the main model.
- \`daily_cost_by_model\` and \`daily_convs_started_by_model\` are keyed by the full model id. One model can appear under several ids (\`global.\`, \`us.\`, \`au.\`, \`apac.\` in front). The dashboard merges them into one row.
- \`daily_convs_by_model\` counts conversation-days, like \`daily_convs\`.

### The costliest conversations

- \`chat.top_conversations\`: the 50 costliest conversations in the snapshot. In the \`conversations\` file.
- Each row is the conversation's whole life: its total cost, turns, messages and tool calls. A row has no figure for one window.
- The dashboard lists a row when the conversation was active inside the window. It still shows lifetime figures, and says so.
- A row has no conversation title. It has the conversation id, the person's id, the agent's id and name, the model and the times.

## Scheduled runs, schedules and time saved

### Three counts have been called "scheduled runs"

They are three different things. Do not mix them.

1. **Run records.** One log file for each run, with a date. \`scheduled_runs.daily_runs\`, \`daily_cost\`, \`daily_failed_runs\`, and \`scheduled_runs.totals.count\`. This is the one count that follows a time window. The dashboard's "Scheduled runs" is this.
2. **A schedule's own counter.** \`schedules.rows[].total_runs\`, summed in \`schedules.total_runs\` and \`impact.total_schedule_runs\`. It has no dates. All time.
3. **Conversations whose id starts \`schedule-\`.** \`chat.daily_scheduled_count\`, \`chat.by_category.agent_run\`, \`chat.by_agent[].scheduled_count\` and \`chat.daily_scheduled_count_by_agent\`. These are conversations, not runs. One run can leave no conversation, and one conversation can be active on several days.

On one stack the three read 2,362, 3,434 and 3,419.

### Run records: \`scheduled_runs\`

- A run is placed on the day it completed. If it has no completion time, the day it started.
- \`by_status\`: \`success\`, \`partial\`, \`failed\`, \`unknown\`. A failed run is \`failed\`. Failure rate is \`daily_failed_runs\` divided by \`daily_runs\` over the same window.
- Run records include runs an automation made of an app. A successful one of those reads \`unknown\` in \`by_status\`.
- A run's cost is the cost of its conversation, priced from tokens.
- \`top_runs\`: the 50 costliest runs. In the \`agents\` file. A row holds no prompt, no schedule label and no error text.
- \`by_agent\`: runs, cost, turns and failed runs for each agent, over the snapshot.
- **Run records do not reach back the same distance on every stack.** A rule that deleted a run log after 90 days was removed in August 2026. Logs deleted before then are gone. So each stack's run records start on a different day. The first day in \`scheduled_runs.daily_runs\` is where a stack's run records start.

### Schedules: \`schedules\`

- "now": \`count\`, \`by_status\`, \`by_derived_status\`, \`schedules_with_errors\`, \`projected_runs_per_month_total\`.
- \`derived_status\` turns a schedule that is switched on into one of three: \`active\` (it repeats), \`scheduled\` (a single run in the future), \`completed\` (a single run in the past). Any other status is kept as it is.
- \`projected_runs_per_month\` is worked out from the schedule's timing rule, for schedules that repeat.
- \`rows\` is in the \`inventory\` file. A row carries the schedule's \`label\`, which a person typed.
- \`rows[].app_id\` is set when the schedule runs an app in place of an agent. Such a row has no agent.

### Time saved: one definition, two numbers

The definition: **each scheduled run of an agent saves the minutes its owner estimated for that agent** (\`estimated_time_saved_minutes\`). Chat with an agent saves nothing by this definition. A run of an app saves nothing.

Both numbers count only a schedule that still exists, whose agent still exists, and both use the agent's minutes as they are set today.

- \`impact.total_time_saved_minutes\`: each schedule's own counter times its agent's minutes. **All time.** It cannot be cut to a window, because a counter has no dates.
- \`impact.daily_time_saved_minutes\`: the same rule over run records, which have dates. This is what a window uses. \`daily_time_saved_minutes_by_agent\` splits it.

**The two do not add up to each other on an older stack.** The daily series holds only runs that still have a run record, and only inside the 420 days. A windowed "time saved" is right when the window is inside the stack's run records. The lifetime total is a different number. Never show one as a part of the other.

Example: an agent with 15 minutes. Its schedule has run 100 times in its life. 40 of those runs have a run record in October.

\`\`\`
total_time_saved_minutes        1,500    = 100 x 15, all time
time saved in October             600    = 40 x 15
\`\`\`

## Agents, knowledge bases, connectors and integrations

All of these are "now": the state at the last nightly update. Do not subtract one night's count from another to find activity.

- \`agents.agents\`: workspace agents. \`agents.user_agents\`: personal agents. Each has a \`count\` and \`rows\` (in the \`inventory\` file). A row has the agent's \`title\`, who made it, and its \`estimated_time_saved_minutes\`.
- \`chat.by_agent\`: use of each agent over the snapshot. \`scheduled_count\` and \`adhoc_count\` are conversations. \`cost\`, \`scheduled_cost\`, \`adhoc_cost\`. \`optimise_sessions\` counts "optimise this agent" sessions, which are also in \`adhoc_count\`.
- Per agent by day: \`daily_cost_by_agent\`, \`daily_scheduled_cost_by_agent\`, \`daily_adhoc_cost_by_agent\` on the event's day. \`daily_scheduled_count_by_agent\` and \`daily_adhoc_count_by_agent\` on the conversation's first request day.
- **One agent id can be in many stacks.** Library agents and system agents have the same id everywhere. In a summed view the dashboard keys an agent by \`<client>::<agent id>\`. Do the same, or one stack's numbers replace another's.
- \`integrations.knowledge_bases\`: \`count\` and \`rows\` with each knowledge base's \`name\`.
- \`integrations.data_connectors\`: \`count\`, \`by_type\`, and \`by_user_count\` keyed by person id.
- \`pipedream\`: integrations people have connected.
  - \`users_queried\`: people the rollup asked about.
  - \`users_with_apps\`: people with one connected integration or more. Use this for "people with an integration".
  - \`users_with_data\`: people the lookup worked for. It says nothing about whether they connected anything. It is kept for older tools.
  - \`active_connections\`, \`distinct_active_apps\`, and \`by_app_active\` (one row per integration with the ids of the people who connected it).

## Credits: \`credits\`

Read from the client's credit ledger. Credits are what a customer is billed in.

- \`available: false\` means the stack does not meter credits. It has no ledger.
- One credit is \`credit_usd\` US dollars. The default is 0.30.
- A **billing month** is a New Zealand calendar month, written \`YYYY-MM\`.
- \`months\`: every billing month on record. For each: \`credits_charged\`, \`credit_revenue_usd\`, \`consumption_cost_usd\`, \`allocation\`, \`settled\`.
- \`current_month\`, \`current_month_used\`, \`current_month_allocation\`, \`current_month_remaining\`: the billing month the snapshot was made in.
- \`standing.available_balance\`: top-ups, settlements and adjustments, less use over the allocation in months that are not settled yet. Nothing stops a client at zero. **Below zero is owed only by a client Arcanum bills for credits.** See "Who owes credits".
- \`daily_credits\` and \`daily_cost_usd\`: by UTC day, for the last four billing months only (\`detail_months\`). A conversation's whole charge lands on the day of its last activity.
- **A window that reaches back past the daily maps.** The dashboard adds the whole of each earlier month that fits inside the window, from \`months\`. A month the window only part covers is left out, and the number says so.
- \`by_source\`: credits by where they were used: \`chat\`, \`agent\`, \`scheduled\`, \`app\`, and others. Over the last four billing months.
- \`by_tier\`: how many conversations were charged mainly at each pricing tier. Over the last four billing months.
- \`first_month\` and \`first_activity_ts\`: when metering started. Use before that was not metered, so do not compare it with a month after.
- \`txns\`: the last 50 top-ups, settlements and adjustments, each with who made it and a note they typed.
- \`top_conversations\`: the 10 conversations that used the most credits in the last four billing months. In the \`conversations\` file. **Each row has the conversation's title.** That is customer content.
- \`billing_admins\`: the people who can see billing, with their email addresses.

A summed view has \`credits_rollup\` in place of \`credits\`. The browser builds it over the stacks whose use the view counts:

- \`current_month_used\` and \`current_month_allocation\`: the current billing month.
- \`owed_total\`: the balances below zero of the clients Arcanum bills for credits, added, as a number above zero.
- \`prepaid_total\`: the balances above zero of the same clients, added.
- \`collectible_stacks\`: how many of those clients the view holds.
- \`not_billed_over\`: credits used this month past the allocation by every other metered stack. Nobody owes these.
- \`daily_credits\`, \`by_source\`, \`months\`.
- \`stacks_behind\`: stacks left out of this month's usage because their last update is from an earlier month.

### Who owes credits

A client owes credits only when Arcanum can invoice it for them. The dashboard calls such a client **collectible**. The same rule is \`isCollectible\` in the portal and \`collectible.py\` in the rollup:

- The contract is Credits, or Partner with the client billed as Credits (\`contract_type\` is \`credits\`).
- The client is not tagged Inactive.

Every other client meters credits and can end a month below zero, and owes nothing: legacy, own account, trial, internal, and a partner billed another way. Their use past the allocation is "past allocation, not billed".

The dashboard reads each part from here:

- **Contract and partner**: the portal's live record first. Else \`client_config.contract\` and \`partner\` in the snapshot. Else \`client_config.contract_type\`.
- **Billing**: \`client_config.contract_type\`.
- **Inactive**: the portal's live tag. \`client_config.inactive\` and \`client_config.collectible\` are the rollup's copy from the night of the snapshot.

## Apps and dashboards: \`user_apps\`

Apps and dashboards that a client's own people build and publish. Apps that Arcanum ships with the product are not here. Their only trace in a snapshot is their AWS cost.

### Schema 1 and schema 2

- \`schema\` missing or 1: an older snapshot. It holds the inventory counts and a 7-day run count, and no history. The run count on schema 1 is incorrect: it counted bookkeeping rows, draft runs and test runs as runs. Do not use it.
- \`schema: 2\`: the block described here.

### Missing is not zero

- \`sources\` says which of the client's records the rollup could read: \`runs\`, \`named_shares\`, \`public_links\`, \`public_views\`, \`app_data\`, \`automations\`, \`automation_runs\`, \`credits\`, \`chat\`. Each is true or false.
- When a source is false, the keys it feeds are **left out of the block**. Show "not available". Never show 0.
- A key that is present with 0 is a real zero: the source was read and held nothing.
- A \`daily_by_app\` series for a source that was read is always present. It is empty when no app has a day.
- \`users\` is left out when the client has no run record.
- **The one exception: optional keys on an app's row.** \`version\`, \`first_published_at\` and \`last_published_at\` are left out for an app that was never published. \`last_run_at\` is left out when no run is held. \`title\` is left out when the record has none. There, missing means "none".
- \`as_of\`: the UTC day the block was built.

### The inventory: \`user_apps.apps\`

All "now". The counts are on two bases.

**Over every app record**, a draft that was never published included:

- \`total\`, \`apps\`, \`dashboards\`: every record, and how many are apps and how many are dashboards.
- \`published\`, \`draft_only\`: with a published version, and without one.
- \`statics\`: pages with no code behind them.
- \`with_public_link\`, \`distinct_owners\`, \`versions_published\`.

**Over published apps only**, because a draft that nobody else can open is not "shared" or "live":

- \`published_apps\`, \`published_dashboards\`. They add up to \`published\`.
- \`live\` (code that draws a page) and \`headless\` (code with no page).
- \`by_visibility\`: \`personal\`, \`shared\`, \`company\`, \`company_editor\`.
- \`shared\`: someone other than the owner can open it inside the company.
- \`named_shares\`: shared with one named person or group, or more.
- \`with_public_intake\`: a public form that takes submissions.
- \`with_entrypoints\`, \`entrypoints_by_kind\` (\`schedule\`, \`event\`, \`send_to\`), \`with_integrations\`.
- \`with_automation\`, \`automations\`, \`automations_active\`: automations that run a published app.

Take care with \`statics\`. It is on the every-record base. \`live + headless + statics\` equals \`published\` only when no static page is a draft.

Example, one dev stack on 13 October 2026: total 60, published 49 (34 apps and 15 dashboards), draft only 11, live 41, shared 21, with a public link 10, with an automation 1.

### What counts as a run

- **A run** is a run of a published version, started by a person or by one of the app's entry points (a schedule, an event, or an item sent to the app).
- **A build run** is a draft run or a test run. It is in \`build_runs\`, never in \`runs\`. A test run is kept for 24 hours only, so \`build_runs\` is a floor.
- **Bookkeeping rows** in the client's run record are never counted.
- **People** counts only runs a person started. That includes an item a person sends to an app. A schedule's run is not its owner using the app.

### History starts the night schema 2 shipped

The client keeps a run for 7 days. No older record of runs exists anywhere. So the rollup keeps its own history: each night it takes the last 7 UTC days from the client and every older day from the snapshot it wrote the night before.

- \`history_from\`: the first day from which the run counts are complete. **No run history exists before the night schema 2 first ran for the stack.** A window that starts before \`history_from\` is only part covered.
- \`history_from\` can move forward. If the rollup misses more than 6 nights for a stack, the days in the gap were never read in full, and complete history starts again.
- \`runs.count_7d\` and \`totals.d7\` cover the last 7 UTC days, today so far included.

Example: schema 2 first ran for a stack on 14 October, by the UTC day.

\`\`\`
history_from              "2026-10-08"    6 days before, as far back as the client's record reached
runs in 1 to 31 October   covers 8 to 31 October only
runs in September         not available
\`\`\`

### By day: \`user_apps.daily\`

Each is \`{day: n}\`.

- \`runs\`, \`build_runs\`.
- \`users\`: people who ran an app that day. \`apps_run\`: apps with a run that day. **Never add these two up over days.** Use \`totals\`, or \`user_apps.users\` and \`daily_by_app\`, for a count over a window.
- \`automation_runs\`, \`automation_failures\`: from the scheduled-run logs, which have their own history.
- \`credits\`, \`cost_usd\`, \`ai_runs\`: from the credit ledger. One ledger row for each run that made an AI call. Only AI steps are charged.
- \`publishes\`, \`new_apps\`, \`deleted_apps\`. A deleted app is counted on the night the rollup first finds it gone.
- \`public_views\`, \`submissions\`: views of a public link, and submissions of a public form.
- \`build_cost_usd\`: the model spend of App Builder conversations.

### The rest of the block

- \`totals.d7\`, \`d30\`, \`d90\`: the last 7, 30 and 90 days up to and including \`as_of\`: \`runs\`, \`users\`, \`apps_run\`, \`automation_runs\`, \`credits\`, \`cost_usd\`, \`publishes\`, \`new_apps\`. Here \`users\` and \`apps_run\` are counted once each, however many days they were active.
- \`rows\` (in the \`apps\` file): one row per app. \`id\`, \`title\`, \`type\` (\`app\` or \`dashboard\`), \`kind\` (\`live\`, \`static\`, \`headless\`), \`owner\` (a person's id), \`visibility\`, \`public_link\`, \`published\`, \`version\`, \`versions\`, the dates, \`entrypoints\`, \`integrations\`, \`named_shares\`, \`public_intake\`, \`automations\`, \`last_run_at\`, \`runs_7d\`, \`users_7d\`, \`runs\` and \`users\` (over the history held), \`automation_runs\`, \`automation_failures\`, \`credits\`, \`cost_usd\`, \`ai_runs\`, \`public_views\`, \`submissions\`, \`data_items\`, \`data_bytes\`, \`data_users\`, \`build_convs\`, \`build_cost_usd\`, \`chat_convs\`.
- \`daily_by_app\` (in the \`apps\` file): \`{series: {app id: {day: n}}}\` for \`runs\`, \`build_runs\`, \`automation_runs\`, \`automation_failures\`, \`credits\`, \`cost_usd\`, \`ai_runs\`, \`public_views\`, \`submissions\`.
- \`users\` (in the \`apps\` file): for each person who ran an app: \`runs\`, \`apps\`, \`first_day\`, \`last_day\`, \`daily\` (\`{day: n}\`), and \`last_day_by_app\` (\`{app id: day}\`).
- \`data\`: what the apps store. \`scopes\`, \`shared_scopes\`, \`private_scopes\`, \`draft_scopes\`, \`items\`, \`bytes\`, \`files\`. \`private_scopes\` no longer includes draft scopes.
- \`runs\`: \`count_7d\`, \`distinct_workflows\`, \`top_workflows\`. Kept for an HQ dashboard that reads it. \`by_status\` is gone: it never held a true status.
- **Build cost** has no per-app series by day. An App Builder conversation counts for the last app it worked on.

### What is not recorded

These need a change in the client stack. No snapshot has them.

- **The outcome of a run.** Whether a run a person started worked or failed. Automation failures are recorded. Other failures are not.
- **How long a run took.**
- **Who opened an app.** A run is not an open. A person who looks at a page that is already drawn makes no run.

\`client_config.app_builder\` says whether people on the stack can build apps at all. It is true, false, or null on a snapshot that does not carry the switch. Null means unknown, not off.

## Infrastructure accounts

- An Arcanum AWS account with no Numa stack: CI, image hosting, the deployer, the organisation roots, the spare client accounts.
- Its snapshot is named \`_infra:<account name>\`. \`client_config.infra_account\` is true, \`account_alias\` is the account's name, and \`payer_account_id\` is the account the bill was read through.
- It holds \`cost_explorer\` and empty blocks for everything else. It has no \`bedrock_usage_type\` figures.
- An account that cost nothing in the window gets no snapshot that night.
- It is in "Arcanum's bill", "Infrastructure" and "Whole fleet". It is never in a customer view, in the Nolia or partner views, on the health map, or in the Clients table.
- The dashboard shows its bill as AWS billed it. Model lines stay in.

## How a view is added up

The fleet-wide views, and exactly which snapshots each one sums. The sidebar lists them in three groups.

Customers:

- **All customers** (\`_CLIENTS\`): every customer stack, Nolia stacks included. Not dev stacks, not HQ, not infrastructure accounts.
- **NextGen** (\`_NEXTGEN\`): stacks whose \`account_org\` is \`nextgen\`. Dev stacks and Nolia stacks in that organisation are included.
- **Standalone** (\`_STANDALONE\`): stacks whose \`account_org\` is \`standalone\`.

Nolia and partners:

- **Nolia** (\`_NOLIA\`): every Nolia stack, dev stacks included.
- **One view for each partner** (\`_PARTNER:<partner>\`, the partner's name in lower case): stacks on the Partner contract through that partner. The views are built from the data, so a new partner gets a view by itself. A partner named Nolia gets none: the Nolia view holds its stacks.

Arcanum:

- **Arcanum's bill** (\`_ARCANUM\`): NextGen stacks, Arcanum's own stacks (HQ, dev, demo) and infrastructure accounts. Nolia stacks are left out. A stack with no \`account_org\` on record is left out too.
- **Infrastructure** (\`_ARCANUM_INFRA\`): infrastructure accounts only.
- **Whole fleet** (\`_FLEET\`): every snapshot. Its spend matches the AWS bill.

What kind a stack is:

- **dev**: \`client_config.dev_instance\` is true and the stack is not \`hq\`.
- **internal**: \`hq\`, Arcanum's own working Numa.
- **infrastructure**: \`client_config.infra_account\` is true.
- **customer**: everything else.
- **Nolia**: \`client_config.product\` is \`nolia\`. A snapshot with no \`product\` (older, or its update failed) is Nolia when its name starts with \`nolia\`. A Nolia stack is also a customer, dev or internal stack by the rules above.

### Inactive clients

A person tags a client Inactive in the portal. The dashboard reads the tag live, in every view:

- The client is in no list: not the sidebar, not the Clients table, not compare, not the health map, not "Largest stacks".
- It is in no use figure and no credit figure: conversations, people, agents, apps, credits.
- **Its spend stays in.** Its AWS bill and its model use are in every view it belongs to, because the money was spent. A spend figure says how much of it is theirs: "Includes <amount> for N Inactive stacks".
- "Show inactive" in the sidebar brings the client back into the lists and the use and credit figures.
- An Inactive client is never collectible, so it owes no credits.
- A view's counts in the sidebar leave hidden stacks out.

The rules of the sum:

- The AWS bill is taken once for each AWS account, from its cost owner. A standalone customer's bill is cut to Numa's part first.
- Chat, people, schedules, scheduled runs, credits and apps are added for each stack the view counts.
- A hidden Inactive stack adds its spend and nothing else. The bill of an account only such stacks hold, and their model use, are carried in \`inactive_spend\` as well, so spend can say how much is theirs. Model spend on screen is their model use plus the view's \`chat.daily_cost\`. The unit costs (per conversation, per message, per person) divide the view's own model use only.
- Credits owed and prepaid count only collectible clients.
- A figure built from a newer daily field leaves out the stacks that do not carry the field yet, and says which.
- "Credits used this month" leaves out a stack whose record is from an earlier billing month.
- The view's time is its oldest snapshot's time. Stale stacks are named.
- A stored snapshot named \`_FLEET\` or \`_CLIENTS\` is from an old version of the rollup. Ignore it.

## The health map

The health map does not follow the time window. It always shows 30 and 7 days, measured back from each stack's own snapshot day.

- **Ring:** people last active in the 30 days, divided by provisioned people. Half or more is "Power users". A fifth to a half is "Healthy". Under a fifth is "At risk". Nobody active is "Dormant". A stack with few people is pulled toward the average of the stacks drawn before it is placed.
- **Colour:** the sum of \`daily_active_users\` over the 30 days against the 30 days before. More than 20% up is growing. More than 20% down is falling.
- **Arrow:** the same over 7 days against the 7 days before, when the change is more than 20%.
- **Size:** provisioned people.

## What a snapshot holds about people and their content

A snapshot is customer data. An earlier version of this guide said it held no names and no email addresses. That was incorrect.

**A snapshot holds:**

- **Every person's email address**, in \`users.sub_to_email\`, beside their id. Also the email addresses of billing admins, in \`credits.billing_admins\`.
- **A person's id** on every per-person row and daily map, on each of the costliest conversations and runs, on each agent and each app (who made it), and in the lists of who connected which integration.
- **Agent names**, in \`chat.by_agent[].agent_title\`, \`agents.*.rows[].title\`, \`chat.top_conversations[].agent_title\`, \`schedules.rows[].agent_title\` and \`scheduled_runs.top_runs[].agent_title\`.
- **Schedule labels**, in \`schedules.rows[].label\`, with each schedule's timing rule.
- **App and dashboard names**, in \`user_apps.rows[].title\`.
- **Knowledge base names**, in \`integrations.knowledge_bases.rows[].name\`.
- **Conversation titles**, for the 10 conversations that used the most credits, in \`credits.top_conversations[].title\`.
- **Notes people typed on credit top-ups**, in \`credits.txns[].note\`, with who made each one.
- **The names of connected integrations**, for each person, in \`pipedream.per_user_active\`.
- **The first 500 characters of a failed gather's error**, in the index's \`status\`. Credentials are removed.

**A snapshot does not hold:**

- Any message text, from a person or from Numa.
- A title for any conversation in \`chat.top_conversations\`.
- A schedule's prompt.
- The error text of a scheduled run.
- What a tool was sent or what it returned.
- The content of any file.
- A password, a token, or a key for any connected system.

The names and titles above can say what a customer is working on. Do not send an export outside Arcanum.

## The exports

The Export menu makes three files. \`export_kind\` says which one you hold.

### \`raw_snapshots\`

Every snapshot as the rollup wrote it, joined back from the index files. A block a snapshot does not have is filled in empty.

\`\`\`
{
  "export_kind": "raw_snapshots",
  "exported_at": "...",
  "snapshot_count": 322,
  "source": "index",                 // or "dynamodb": the old rows, with 92 days of detail
  "index_generated_at": "...",
  "tiers_not_loaded": [],            // an index file that did not load: its fields are missing
  "note": "...",
  "snapshots": { "<client>": { ...the snapshot } },
  "inferred_costs": { "<client>": { ...total spend, worked out over the client's whole history } }
}
\`\`\`

- \`snapshots\` holds a standalone customer's whole AWS bill. \`inferred_costs\` is worked out on Numa's part of it.
- \`inferred_costs.<client>\` holds \`totals\` (\`ceTotal\` the AWS bill, \`ceClaude\` the model lines, \`chat\` the model spend from tokens, \`inferred\` total spend, \`lent\`, \`borrowed\`), the same by day (\`inferredTotalDaily\` and the rest), \`role\`, and \`modelLinesKnown\`. When \`modelLinesKnown\` is false, \`inferred\` counts model use twice.

### \`dashboard_view\`

The figures the dashboard shows, already worked out, for the stacks and views as on screen. \`schema_version\` is \`"1.2"\`. An Inactive stack the screen hides has no entry of its own. Its spend is in each view's spend.

\`\`\`
{
  "export_kind": "dashboard_view",
  "schema_version": "1.2",
  "window_presets": [7, 14, 30, 60, 90],
  "tiers_loaded": ["users", "agents", ...],
  "views": {
    "<client>" or "_CLIENTS" | "_NEXTGEN" | "_STANDALONE" | "_NOLIA" | "_PARTNER:<partner>"
      | "_ARCANUM" | "_ARCANUM_INFRA" | "_FLEET": {
      "client": "...", "is_aggregate": false,
      "contract": {...},                // a stack: contract, partner, billing, collectible, inactive, product
      "generated_at": "...",            // a summed view: its oldest snapshot
      "newest_generated_at": "...",     // summed views only
      "stale_stacks": [...],            // summed views only
      "window_views": { "7d": {...}, "14d": {...}, "30d": {...}, "60d": {...}, "90d": {...}, "all": {...} }
    }
  }
}
\`\`\`

Each window view:

- \`window\`: start, end, days.
- \`spend\`: \`inferred_total\` (total spend), \`raw_ce_total\` (the AWS bill), \`chat_llm\` (model spend from tokens), \`ce_claude\` (the model lines), \`lent\`, \`borrowed\`, \`role\`, \`model_lines_known\`, \`avg_cost_per_conv\`, \`cost_per_user_msg\`, \`scheduled_runs_count\` and \`scheduled_runs_cost\` (run records). On a summed view that hides Inactive stacks, \`inactive\` holds their part of \`inferred_total\`, \`raw_ce_total\` and \`chat_llm\`, with their names.
- \`volume\`: \`conversations\`, \`adhoc_convs\`, \`scheduled_convs\`, \`user_messages\`, \`tool_calls\`, \`agentic_turns\`. \`scheduled_convs\` is conversations a schedule started. It is not scheduled runs.
- \`operations\`: \`active_users\` (null when the per-person data was not loaded), \`provisioned_users\`, \`cache_hit_ratio_pct\`, \`requests\`, \`errors\`, \`failed_runs\`, \`time_saved_minutes\`, \`active_schedules\`, \`total_schedules\`.
- **\`scopes\`: read this first.** It says, for each figure that can fall back, whether it follows the window (\`window\`) or is a lifetime total (\`all-time\`). \`scopes.conversations\` says whether conversations were counted once, or once for each active day.
- \`cost_composition\`: spend by AWS service, with the model lines replaced by one line for model spend from tokens.
- \`by_category\`, \`by_user_top\` (20), \`by_agent_top\` (20), \`by_tool\`, \`by_model\`.
- \`top_conversations\` and \`top_scheduled_runs\`: up to 50 each. The rows active in the window, with lifetime figures.
- \`credits\`: when a stack in the view meters credits. One stack: \`collectible\`, and \`not_billed_over\` when it is not collectible. A summed view: \`owed\`, \`prepaid\` and \`available_balance\` count collectible clients only; \`collectible_stacks\` and \`not_billed_over\` beside them.
- \`apps\`: when the snapshot has an apps record. In this block a key that is missing or null is not available. It is never zero.
- \`by_client\`: summed views only. One row per stack the view lists. \`ce_total\` is the AWS bill of the stack's account in the window. \`contract\`, \`partner\`, \`billing\`, \`collectible\` and \`product\` say how the client is billed. On a snapshot with no daily record of time saved, \`time_saved_minutes\` is the lifetime total and \`time_saved_scope\` is \`all-time\`.

### \`single_selection\`

The stack or view on screen: \`selection\`, \`selection_label\`, \`is_aggregate\`, \`stale_stacks\`, the \`snapshot\` (one stack only), \`inferred_costs\` for the window on screen, and \`window_views\` for the preset windows, "All", and the window on screen.

## Field reference

Every block of a snapshot. \`[users]\` and the like say which index file the field is in. No mark means \`core\`. "Missing on older snapshots" means: written from the tiered index on.

\`\`\`
client              the client's name, or "_infra:<account name>"
generated_at        when the gather finished
window_days         420

client_config
  client_name, client_account_id, region
  dev_instance                    true for a dev or demo stack, and for hq
  account_org                     "nextgen" | "arcanum" | "standalone" | null
  bedrock_account                 the account this stack borrows model capacity from, if any
  allow_bedrock_quota_sharing     true when the stack lends
  preferred_kb, show_credits, monthly_allocations
  app_builder                     true | false | null (not known)
  contract_type                   credits | legacy | own-account | null (absent, reads as legacy): how the client is billed
  contract                        credits | own-account | trial | partner | internal | legacy | null (not set)
  partner                         the partner a partner client came through, e.g. "Ohnyx"; null otherwise
  product                         "nolia" for a Nolia stack; missing on older snapshots (the name then decides)
  inactive                        true when the client carried the Inactive tag that night; missing on older snapshots
  inactive_reason                 the reason typed on the tag, or null
  collectible                     true when Arcanum can bill the client for credits that night; missing on older snapshots
  infra_account, account_alias, payer_account_id      infrastructure accounts only

cost_explorer
  window {start, end, days}
  grand_total, totals_by_service {service: usd}, totals_by_day {day: usd}
  by_service_daily {service: {day: usd}}                      [services]
  model_lines_daily {day: usd}                                missing on older snapshots
  bedrock_usage_type_totals {usage type: usd}
  bedrock_usage_type_daily {usage type: {day: usd}}           [services] missing on older snapshots
  non_numa_filtered_total, non_numa_filtered_daily            added by the dashboard, standalone only

chat
  window_days, all_time_count
  totals {cost, sdk_cost, convs, turns, requests, user_messages, errors, users, tool_calls,
          input_tokens, output_tokens, cache_read_tokens, cache_creation_tokens}
  by_category {agent_run, agent_chat, plain_chat: {convs, cost, user_messages, turns, tool_calls,
               input_tokens, output_tokens}}
  scheduled_vs_adhoc {scheduled, adhoc: {count, cost, turns, requests, user_messages}}
  tool_totals {tool: n}
  model_totals {model id: {cost, convs}}
  top_conversations [...]                                     [conversations]
  top_conversations_kept, top_conversations_dropped
  by_user {person id: {cost, convs, requests, turns, user_messages, tool_calls,
                       avg_span_seconds, first_at, last_at}}  [users]
  by_agent [{agent_id, agent_title, scheduled_count, adhoc_count, cost, scheduled_cost, adhoc_cost,
             turns, user_messages, tool_calls, first_at, last_at, optimise_sessions,
             last_optimised_at}]                              [agents]

  on the event's own day:
  daily_cost, daily_messages, daily_turns, daily_tool_calls         {day: n}
  daily_active_users                                                {day: people}, never add up
  daily_convs, daily_scheduled_count, daily_adhoc_count             {day: conversation-days}
  daily_cost_by_category, daily_messages_by_category, daily_turns_by_category,
  daily_tool_calls_by_category                                      {category: {day: n}}
  daily_convs_by_category                                           {category: {day: conversation-days}}
  daily_cost_by_model                                               {model id: {day: usd}}
  daily_convs_by_model                                              {model id: {day: conversation-days}}
  daily_cost_by_user, daily_messages_by_user                        [users]
  daily_cost_by_agent, daily_scheduled_cost_by_agent, daily_adhoc_cost_by_agent    [agents]
  daily_requests, daily_errors                                      missing on older snapshots
  daily_input_tokens, daily_output_tokens,
  daily_cache_read_tokens, daily_cache_creation_tokens              missing on older snapshots
  daily_tool_calls_by_tool {tool: {day: n}}                         missing on older snapshots
  daily_turns_by_user, daily_tool_calls_by_user                     [users] missing on older snapshots

  on the conversation's first request day (all missing on older snapshots):
  daily_convs_started                                               {day: conversations}
  daily_convs_started_by_category, daily_convs_started_by_model     {key: {day: n}}
  daily_convs_started_by_user                                       [users]
  daily_span_seconds_sum, daily_span_count                          {day: n}
  daily_span_seconds_sum_by_user, daily_span_count_by_user          [users]
  daily_scheduled_count_by_agent, daily_adhoc_count_by_agent        [agents] conversations, not runs

schedules
  count, total_runs, schedules_with_errors, projected_runs_per_month_total
  by_status, by_derived_status, by_event_type, by_trigger
  schedule_to_agent {schedule id: agent id}                         [inventory]
  rows [{schedule_id, user_id, agent_id, agent_title, app_id, label, status, derived_status,
         trigger_type, cron_expression, cron_kind, cron_state, projected_runs_per_month,
         is_one_off, total_runs, max_runs, last_run_epoch, last_status,
         last_error_present}]                                       [inventory]

scheduled_runs
  discovered, fetch_errors
  totals {count, cost, turns}
  by_status {success, partial, failed, unknown}
  daily_runs, daily_cost                                            {day: n}
  daily_failed_runs                                                 missing on older snapshots
  by_agent {agent id: {agent_id, agent_title, runs, cost, turns, errors}}    [agents]
  top_runs [...]                                                    [agents]
  top_runs_kept, top_runs_dropped

agents
  agents {count, rows [...]}                                        rows [inventory]
  user_agents {count, rows [...]}                                   rows [inventory]
    a row: agent_id, title, visibility, agent_type, estimated_time_saved_minutes, version,
           created_by_user_id, created_at, updated_at

integrations
  knowledge_bases {count, rows [{kb_id, name, kind}]}               rows [inventory]
  data_connectors {count, by_type, by_user_count}
  mcp-tool-policies {count}, global-data-connector-settings {count}, integrations-approval {count}

users
  user_pool_id, user_pool_name
  provisioned_users
  modified_in_last_30d                                              not active people
  sub_to_email {person id: email}                                   [users]

pipedream
  users_queried, users_with_data, users_with_apps, users_with_errors
  active_connections, distinct_active_apps
  by_app_active [{app_name, user_count, user_subs}]                 [inventory]
  per_user_active {person id: [integration]}                        [inventory]

credits
  available, reason, config_present, credit_usd
  current_month, current_month_allocation, current_month_used, current_month_remaining
  standing {settled_balance, live_overflow, available_balance}
  months {YYYY-MM: {credits_charged, credit_revenue_usd, consumption_cost_usd, allocation,
                    settled, margin_vs_consumption}}
  daily_credits, daily_cost_usd                                     {day: n}, last four billing months
  by_source, by_tier, detail_months, first_month, first_activity_ts
  totals {credits_charged, consumption_cost_usd, credit_revenue_usd, detail_conversations}
  txns [{kind, credits, created_at, created_by, note, month}]
  top_conversations [{conversation_id, credits, cost_usd, source, tier, month, agent_id,
                      title}]                                       [conversations]
  billing_admin_count, billing_admins [{email, sub}]

user_apps                                                           schema 2
  available, schema, as_of, history_from, sources {...}
  apps {...the inventory}
  totals {d7, d30, d90}
  daily {runs, users, apps_run, build_runs, automation_runs, automation_failures, credits,
         cost_usd, ai_runs, publishes, new_apps, deleted_apps, public_views, submissions,
         build_cost_usd}                                            each {day: n}
  runs {available, count_7d, distinct_workflows, top_workflows}
  data {available, scopes, shared_scopes, private_scopes, draft_scopes, items, bytes, files}
  rows [...]                                                        [apps]
  daily_by_app {series: {app id: {day: n}}}                         [apps]
  users {person id: {runs, apps, first_day, last_day, daily, last_day_by_app}}    [apps]

impact
  total_time_saved_minutes                                          all time
  daily_time_saved_minutes {day: minutes}                           missing on older snapshots
  daily_time_saved_minutes_by_agent {agent id: {day: minutes}}      [agents] missing on older snapshots
  per_agent_runs {agent id: n}                                      [agents] schedule counters, all time
  total_schedule_runs                                               schedule counters, all time
  total_agents, total_schedules, active_schedules
\`\`\`

The dashboard sets three more fields while it works. The rollup never writes them, and they are not in the raw export.

- \`portal\`: which index files are loaded into the object.
- On a row in a summed view: \`client\`, the stack the row came from.
- On an agent row in a summed view: \`key\`, \`<client>::<agent id>\`.

## What changed from the earlier guide

If you hold an older copy of this guide, these parts of it were incorrect or are out of date.

- **Where the data is.** The old guide said the fleet views read a cut copy of each snapshot from DynamoDB. They read the index in S3. Nothing is cut.
- **When it runs.** 01:00 New Zealand time. Not 00:00 UTC.
- **Conversations.** The old guide's \`daily_convs\` is conversation-days. Use \`daily_convs_started\`.
- **No conversation list.** The old guide spoke of "conversations dropped" from the cut copy. No snapshot, cut or complete, holds a list of every conversation. It holds the 50 costliest.
- **Time saved.** The old guide said it was the agent's minutes times \`scheduled_runs.totals.count\`, scaled to the window by the share of runs. The total is each schedule's own counter times its agent's minutes. A window uses the daily series. Nothing is scaled.
- **A note that was never there.** The old guide said the dashboard carries a note about late AWS bill days under its spend figures. It did not. The fact itself holds: the bill stops a day or two before today.
- **Privacy.** The old guide said a snapshot holds no email addresses and no titles. It holds both.
- **Credits by source.** \`by_source\` has \`app\` as well as \`chat\`, \`agent\` and \`scheduled\`.
- **Standalone customers.** The model lines the filter keeps include the OpenAI lines, not only the Claude lines.
- **Arcanum's bill.** The old guide said it was everything that is not standalone. It is NextGen, Arcanum's own stacks and the infrastructure accounts. Nolia stacks and stacks with no organisation on record are left out.
- **Credits owed.** The old guide said a balance below zero means credits are owed. Only a collectible client owes them.
- **Inactive clients.** The customer views used to drop an Inactive client whole, and the Arcanum views kept it whole. Every view now hides it and keeps its spend.

## Where the logic lives

- The gather: \`lambdas/python/numa-fleet-analytics-rollup/numa_fleet_analytics_rollup/gather/\`. \`chat.py\` for pricing and the daily series, \`cost_explorer.py\`, \`scheduled_runs.py\`, \`schedules.py\`, \`credits.py\`, \`apps.py\`, and \`__init__.py\` for time saved.
- The index: \`fleet_index.py\` in the same package.
- The sums and the standalone filter: \`numa-customer-success-portal/src/components/NumaDashboard/shared.ts\`.
- Total spend: \`inferredCosts.ts\` beside it.
- Each stack's contract and whether it is collectible: \`contracts.ts\` beside it, from the rules in \`src/config/contract.ts\`.
- Every figure on screen: \`selectors.ts\` beside it.
- The storage and field contract: \`documentation/fleet-analytics/README.md\`.
`;

/** The guide, drawn for a page or for the body of a modal. It brings no frame of its own. */
export function DataGuide() {
  return (
    <div className="data-guide">
      <ReactMarkdown>{README_MARKDOWN}</ReactMarkdown>
    </div>
  );
}
